<script setup lang="ts">
import router from '@/router';
import { useI18n } from "vue-i18n";
import { useDisplay } from 'vuetify'
import {
  reactive,
  ref,
  computed,
  markRaw
} from "vue";
import { VDateInput } from "vuetify/labs/components";
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

import MainTitleComponent from "@/components/ui/MainTitleComponent.vue";
import DividerComponent from "@/components/ui/DividerComponent.vue";
import AssetComponent from '@/components/mandate/AssetComponent.vue';
import DocumentComponent from '@/components/mandate/DocumentComponent.vue';
import DefaultModal from "@/components/ui/DefaultModal.vue";
import MandateService from "@/services/mandate/mandateService.ts";
import { useSnackbarStore } from "@/stores/snackbarStore.ts";
import validators from "@/utils/validators.ts";
import { displayToast } from '@/utils/toastUtils.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import {
  formatDate,
  formatPercentage
} from '@/utils/formatters.ts';
import {
  MandateBoStatusEnum,
  MandateDocumentCategoryEnum
} from "@/enums/mandate_enum.ts";
import { PeriodicityKey } from "@/enums/periodicity_enum.ts";

import type {
  CreateMandateRequest,
  AssetRow,
  MandateCurrency,
  MandateDocument,
  MandateInvestmentLimit,
  MandateResponse
} from '@/types/services/mandate/mandate.type.ts';
import type { ValidationRule } from "@/types/utils/validators.type.ts";
import type { DataTableHeader } from 'vuetify/framework';
import { useRoute } from 'vue-router';
import type { AploAsset } from '@/types/services/aplo/aplo.type.ts';

const { t } = useI18n()
const route = useRoute()
const { smAndDown } = useDisplay()
const snackbarStore = useSnackbarStore()

const step = ref<number>(1)
const loading = ref<boolean>(false)

// -- Mandate data from route meta --//
const assetNames = ref<AploAsset[]>(route.meta.assets as AploAsset[] || [])
const investmentLimit = ref<MandateInvestmentLimit>(route.meta.investmentLimit as MandateInvestmentLimit
  || { minInvestment: 0, maxInvestment: 0 }
);

//--- Create mandate form ---//
const nameRules = ref<ValidationRule[]>([...validators.mandateNameRules])
const minimumInvestmentRules = computed<ValidationRule[]>(() =>
  validators.mandateMinimumInvestmentRules(investmentLimit.value.minInvestment)
)
const maximumInvestmentRules = computed<ValidationRule[]>(() =>
  validators.mandateMaximumInvestmentRules(
    investmentLimit.value.minInvestment + 1,
    investmentLimit.value.maxInvestment
  )
)
const recommendedDurationRules = ref<ValidationRule[]>([...validators.mandateRecommendedDurationRules])
const availableAtRules = ref<ValidationRule[]>([...validators.mandateAvailableAtRules])
const youtubeRules = ref<ValidationRule[]>([...validators.mandateYoutubeRules])
const percentageRules = ref<ValidationRule[]>([...validators.mandatePercentageRules])

const btcAsset = assetNames.value.find(asset => asset.name === 'BTC')
const currencies = ref<MandateCurrency[]>(["EUR"])

const form = reactive<CreateMandateRequest>({
  id: null,
  name: null,
  description: null,
  assets: btcAsset ? [{ id: null, name: btcAsset.name, percentage: 100 }] : [],
  risk: 1,
  currency: currencies.value[0],
  minimumInvestment: investmentLimit.value.minInvestment,
  maximumInvestment: investmentLimit.value.minInvestment + 1000 - investmentLimit.value.minInvestment,
  recommendedDuration: 1,
  availableAt: Date(),
  closedAt: null,
  strategyOperation: null,
  presentation: null,
  youtubeUrl: null,
  isDraft: true,
  fees: {
    subscriptionFee: 0.0,
    managementFee: 0.0,
    managementPeriodicity: PeriodicityKey.MONTHLY,
    exitFee: 0.0,
    earlyExitPenalty: 0.0,
    earlyExitMinDuration: 0,
    performanceFee: 0.0
  }
})

const availableAssetNames = (index: number | null = null) => {
  const selected = form.assets.map((asset, assetIndex) =>
    assetIndex !== index ? asset.name : null
  ).filter(Boolean)
  return assetNames.value.filter(asset => !selected.includes(asset.name))
}
const canAddAsset = computed(() => form.assets.length < assetNames.value.length)
const totalPercentage = computed<number>(() =>
  form.assets.reduce((sum, asset) => sum + (Number(asset.percentage) || 0), 0)
)

const documents = reactive<MandateDocument[]>([])

const assetHeaders = computed<DataTableHeader[]>(() => [
  {
    title: t("views.createMandate.table.asset.name"),
    key: "name",
    align: "start",
    sortable: false
  },
  {
    title: t("views.createMandate.table.asset.percentage"),
    key: "percentage",
    align: "start",
    sortable: false
  },
  {
    title: t('views.createMandate.table.asset.action'),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  }
])
async function addAsset(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(AssetComponent),
    props: {
      availableAssets: availableAssetNames(),
      initial: null
    },
  })

  if (modal && typeof modal === 'object') {
    const { id, name, percentage } = modal as AssetRow;
    form.assets.push({ id, name, percentage })
  }
}
async function editAsset(index: number): Promise<void> {
  const current = form.assets[index]

  const modal = await displayModal({
    component: markRaw(AssetComponent),
    props: {
      availableAssets: availableAssetNames(index),
      initial: { ...current }
    },
  })

  if (modal && typeof modal === 'object') {
    const { id, name, percentage } = modal as AssetRow;
    form.assets[index] = { id, name, percentage }
  }
}
function removeAsset(index: number): void {
  form.assets.splice(index, 1)
}

const documentHeaders = computed<DataTableHeader[]>(() => [
  {
    title: t("views.createMandate.table.document.name"),
    key: "name",
    align: "start",
    sortable: false
  },
  {
    title: t("views.createMandate.table.document.category"),
    key: "category",
    align: "start",
    sortable: false
  },
  {
    title: t("views.createMandate.table.document.createdAt"),
    key: "createdAt",
    align: "start",
    sortable: false
  },
  {
    title: t('views.createMandate.table.document.action'),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  }
])
async function addDocument(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(DocumentComponent)
  })

  if (modal && typeof modal === 'object') {
    const { id, name, category, file } = modal as MandateDocument;
    const document = { id, name, category, created_at: new Date().toISOString(), file }

    if (documents.some(
      (doc) => doc.category === MandateDocumentCategoryEnum.DIC) &&
      document.category === MandateDocumentCategoryEnum.DIC) {
      displayToast(t('views.createMandate.dicAlreadyAdded'), 'error');
      return
    }

    if (form.id) {
      const response = await MandateService.uploadDocument(form.id, [document])
      document.id = response[0].id
      displayToast(t('views.createMandate.uploadDocumentSuccess'), 'success');
    }

    documents.unshift(document)
  }
}
async function removeDocument(document: MandateDocument) {
  if (form.id && document.id) {
    const modal = await displayModal({
      component: markRaw(DefaultModal),
      title: t('views.createMandate.deleteModal.title',),
      message: t('views.createMandate.deleteModal.message', { document: document.name }),
    });

    if (modal) {
      await MandateService.deleteDocument(form.id, document.id);
      displayToast(t('views.createMandate.deleteDocumentSuccess'), 'success');
      documents.splice(documents.indexOf(document), 1)
    }
  } else {
    documents.splice(documents.indexOf(document), 1)
  }
}
function categoryLabel(category: string) {
  return t(`views.createMandate.table.document.label.${category}`)
}

const canSubmitToReview = computed<boolean>(() => {
  return Boolean(
    !!form.name &&
    !!form.description &&
    form.assets.length > 0 &&
    form.risk != null &&
    !!form.currency &&
    form.minimumInvestment != null &&
    form.maximumInvestment != null &&
    form.recommendedDuration != null &&
    !!form.availableAt &&
    !!form.strategyOperation &&
    !!form.presentation &&
    documents.length > 0 &&
    documents.some(doc => doc.category === MandateDocumentCategoryEnum.DIC) &&
    totalPercentage.value === 100 &&
    form.fees.subscriptionFee != null &&
    form.fees.managementFee != null &&
    form.fees.exitFee != null &&
    form.fees.earlyExitPenalty != null &&
    form.fees.earlyExitMinDuration != null &&
    form.fees.performanceFee != null
  )
})

async function saveMandate(isDraft: boolean): Promise<void> {
  loading.value = true
  form.isDraft = isDraft

  const mandate: CreateMandateRequest = {
    id: form.id,
    name: form.name,
    description: form.description,
    assets: form.assets,
    risk: form.risk,
    currency: form.currency,
    minimumInvestment: form.minimumInvestment,
    maximumInvestment: form.maximumInvestment,
    recommendedDuration: form.recommendedDuration,
    strategyOperation: form.strategyOperation,
    presentation: form.presentation,
    availableAt: form.availableAt ? new Date(form.availableAt).toISOString() : "",
    closedAt: form.closedAt ? new Date(form.closedAt).toISOString() : "",
    youtubeUrl: form.youtubeUrl,
    isDraft: isDraft,
    fees: {
      subscriptionFee: Number(form.fees.subscriptionFee),
      managementFee: Number(form.fees.managementFee),
      managementPeriodicity: form.fees.managementPeriodicity,
      exitFee: Number(form.fees.exitFee),
      earlyExitPenalty: Number(form.fees.earlyExitPenalty),
      earlyExitMinDuration: Number(form.fees.earlyExitMinDuration),
      performanceFee: Number(form.fees.performanceFee)
    }
  }

  if (!form.name) {
    snackbarStore.openSnackbar({
      text: t("views.createMandate.snackbar.nameRequired"),
      color: "error"
    })
    loading.value = false
    return
  }

  try {
    let response: MandateResponse

    if (mandate.id) {
      response = await MandateService.updateMandate({
        ...mandate,
        boStatus: MandateBoStatusEnum.PENDING
      })
    } else {
      response = await MandateService.createMandate(mandate)
      mandate.id = response.id

      if (documents.length > 0 && mandate.id) {
        const response = await MandateService.uploadDocument(mandate.id, documents)
        documents.forEach((document, index) => {
          document.id = response[index].id
        })
      }
    }

    if (!isDraft) {
      await router.push('/mandates')
    } else {
      Object.assign(form, {
        ...response,
        minimumInvestment: response.minimumInvestment / 100,
        maximumInvestment: response.maximumInvestment / 100,
        availableAt: response.availableAt ? new Date(response.availableAt) : null,
        closedAt: response.closedAt ? new Date(response.closedAt) : null,
        fees: {
          subscriptionFee: Number(response.fees.subscriptionFee ?? 0),
          managementFee: Number(response.fees.managementFee ?? 0),
          exitFee: Number(response.fees.exitFee ?? 0),
          earlyExitPenalty: Number(response.fees.earlyExitPenalty ?? 0),
          earlyExitMinDuration: response.fees.earlyExitMinDuration,
          performanceFee: Number(response.fees.performanceFee ?? 0),
        }
      })
    }

    if (mandate.id) {
      snackbarStore.openSnackbar({
        text: t("views.createMandate.snackbar.update"),
        color: "success"
      })
    } else {
      snackbarStore.openSnackbar({
        text: t("views.createMandate.snackbar.create"),
        color: "success"
      })
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent>
    {{ t("views.createMandate.title") }}

    <template #actions>
      <v-col cols="12" sm="auto" class="d-flex flex-column flex-sm-row">
        <v-btn
          :loading="loading"
          :disabled="loading"
          class="mr-0 mr-sm-4 mb-4 mb-sm-0"
          type="submit"
          variant="outlined"
          @click="saveMandate(true)"
        >
          {{ t("views.createMandate.buttons.draft") }}
          <template #prepend>
            <v-icon icon="PhPencilSimple"/>
          </template>
        </v-btn>

        <v-btn
          :disabled="!canSubmitToReview || loading"
          :loading="loading"
          type="submit"
          @click="saveMandate(false)"
        >
          {{ t("views.createMandate.buttons.create") }}
          <template #prepend>
            <v-icon icon="PhCheck"/>
          </template>
        </v-btn>
      </v-col>
    </template>
  </MainTitleComponent>

  <v-stepper v-model="step" :elevation="0" :mobile="smAndDown">
    <v-stepper-header>
      <v-divider />

      <v-stepper-item
        :title="t('views.createMandate.stepper.step1')"
        :value="1"
        color="primary"
        editable
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.createMandate.stepper.step2')"
        :value="2"
        color="primary"
        editable
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.createMandate.stepper.step3')"
        :value="3"
        color="primary"
        editable
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.createMandate.stepper.step4')"
        :value="4"
        color="primary"
        editable
      />

      <v-divider />
    </v-stepper-header>

    <v-stepper-window>
      <!-- STEP 1 -->
      <v-stepper-window-item :value="1">
        <v-row>
          <v-col cols="12" class="d-flex align-center">
            <v-icon class="mr-2" color="primary" icon="PhInfo"/>
            <h2 class="subtitle">
              {{ t("views.createMandate.subtitle1") }}
            </h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="form.name"
              :label="t('views.createMandate.inputs.name.label')"
              :placeholder="t('views.createMandate.inputs.name.placeholder')"
              :rules="nameRules"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="form.currency"
              :items="currencies"
              :label="t('views.createMandate.inputs.currency.label')"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-date-input
              v-model="form.availableAt"
              :label="t('views.createMandate.inputs.availableAt.label')"
              :placeholder="t('views.createMandate.inputs.availableAt.placeholder')"
              :rules="availableAtRules"
              hide-details="auto"
              persistent-placeholder
              prepend-icon=""
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-date-input
              v-model="form.closedAt"
              :label="t('views.createMandate.inputs.closedAt.label')"
              :placeholder="t('views.createMandate.inputs.closedAt.placeholder')"
              disabled
              hide-details="auto"
              persistent-placeholder
              prepend-icon=""
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="form.risk"
              :label="t('views.createMandate.inputs.risk.label')"
              :items="[1, 2, 3, 4, 5, 6, 7]"
              color="primary"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="form.recommendedDuration"
              :label="t('views.createMandate.inputs.recommendedDuration.label')"
              :min="1"
              :rules="recommendedDurationRules"
              :suffix="t('views.createMandate.inputs.recommendedDuration.suffix', form.recommendedDuration)"
              density="compact"
              hide-details="auto"
              type="number"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="form.minimumInvestment"
              :label="t('views.createMandate.inputs.minimumInvestment.label')"
              :min="0"
              :rules="minimumInvestmentRules"
              density="compact"
              hide-details="auto"
              type="number"
            >
              <template #append-inner>
                <v-icon icon="PhCurrencyEur"/>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="form.maximumInvestment"
              :label="t('views.createMandate.inputs.maximumInvestment.label')"
              :min="1"
              :rules="maximumInvestmentRules"
              density="compact"
              hide-details="auto"
              type="number"
            >
              <template #append-inner>
                <v-icon icon="PhCurrencyEur"/>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <QuillEditor
              v-model:content="form.description"
              :placeholder="t('views.createMandate.inputs.description.placeholder')"
              :toolbar="[
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['clean']
              ]"
              content-type="html"
            />
          </v-col>
          <v-col cols="12" md="6">
            <QuillEditor
              v-model:content="form.strategyOperation"
              :placeholder="t('views.createMandate.inputs.strategyOperation.placeholder')"
              :toolbar="[
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['clean']
              ]"
              content-type="html"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <QuillEditor
              v-model:content="form.presentation"
              :placeholder="t('views.createMandate.inputs.presentation.placeholder')"
              :toolbar="[
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['clean']
              ]"
              content-type="html"
            />
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhYoutubeLogo"/>
              <h2 class="subtitle">{{ t("views.createMandate.subtitle2") }}</h2>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.youtubeUrl"
              :label="t('views.createMandate.inputs.youtube.label')"
              :placeholder="t('views.createMandate.inputs.youtube.placeholder')"
              :rules="youtubeRules"
              hide-details="auto"
            />
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 2 -->
      <v-stepper-window-item :value="2">
        <v-row>
          <v-col cols="12" class="d-flex flex-column flex-sm-row justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2" icon="PhMoney"/>
              <h2 class="subtitle mr-2">
                {{ t("views.createMandate.subtitle3") }}
              </h2>
              <v-chip
                :color="totalPercentage === 100 ? 'success' : (totalPercentage > 100 ? 'error' : 'warning')"
                size="small"
              >
                {{ totalPercentage }}%
              </v-chip>
            </div>

            <v-btn
              :disabled="!canAddAsset"
              class="mt-4 mt-sm-0"
              type="submit"
              @click="canAddAsset && addAsset()"
            >
              {{ t("views.createMandate.addAsset") }}
              <template #prepend>
                <v-icon icon="PhPlus"/>
              </template>
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-data-table
              :headers="assetHeaders"
              :items="form.assets"
              :items-per-page="-1"
              :no-data-text="t('common.noResult')"
              class="rounded-std"
              hover
            >
              <template #item="{ item, index}">
                <tr @click="editAsset(index)" style="cursor: pointer">
                  <td>{{ item.name }}</td>
                  <td>{{ formatPercentage(item.percentage) }} %</td>
                  <td>
                    <v-btn
                      :disabled="form.assets.length === 1"
                      variant="plain"
                      @click.stop.prevent="removeAsset(index)"
                    >
                      <v-icon icon="PhTrash"/>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template #bottom />
            </v-data-table>
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 3 -->
      <v-stepper-window-item :value="3">
        <v-row>
          <v-col cols="12" class="d-flex flex-column flex-sm-row justify-space-between">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhFileText"/>
              <h2 class="subtitle">
                {{ t("views.createMandate.subtitle4") }}
              </h2>
            </div>

            <v-btn
              class="mt-4 mt-sm-0"
              type="submit"
              @click="addDocument"
            >
              <v-icon icon="PhPlus" class="mr-1" />
              {{ t('views.createMandate.buttons.addDocument') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-data-table
              :headers="documentHeaders"
              :items="documents"
              :items-per-page="-1"
              :no-data-text="t('common.noResult')"
              class="rounded-std"
              hover
            >
              <template #item="{ item }">
                <tr>
                  <td>{{ item.name }}</td>
                  <td>{{ categoryLabel(item.category) }}</td>
                  <td>{{ formatDate(item.created_at) }}</td>
                  <td>
                    <v-btn
                      variant="plain"
                      @click.stop.prevent="removeDocument(item)"
                    >
                      <v-icon icon="PhTrash"/>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template #bottom />
            </v-data-table>
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 4 -->
      <v-stepper-window-item :value="4">
        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhSignIn"/>
            <h2 class="subtitle">{{ t("views.createMandate.subtitle5") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.subscriptionFee"
              :label="t('views.createMandate.inputs.subscriptionFee.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhReceipt"/>
            <h2 class="subtitle">{{ t("views.createMandate.subtitle6") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.managementFee"
              :label="t('views.createMandate.inputs.managementFee.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
          <v-col cols="12" md="3" sm="6">
            <v-select
              v-model="form.fees.managementPeriodicity"
              :label="t('views.createMandate.inputs.managementPeriodicity.label')"
              :items="[
                { title: t('common.periodicity.monthly'), value: PeriodicityKey.MONTHLY },
                { title: t('common.periodicity.quarterly'), value: PeriodicityKey.QUARTERLY },
                { title: t('common.periodicity.semiannual'), value: PeriodicityKey.SEMIANNUAL },
                { title: t('common.periodicity.yearly'), value: PeriodicityKey.YEARLY },
              ]"
              item-title="title"
              item-value="value"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.performanceFee"
              :label="t('views.createMandate.inputs.performanceFee.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhSignOut"/>
            <h2 class="subtitle">{{ t("views.createMandate.subtitle7") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.exitFee"
              :label="t('views.createMandate.inputs.exitFee.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.earlyExitPenalty"
              :label="t('views.createMandate.inputs.earlyExitPenalty.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.earlyExitMinDuration"
              :disabled="!form.fees.earlyExitPenalty"
              :label="t('views.createMandate.inputs.earlyExitMinDuration.label')"
              :min="0"
              :suffix="t('views.createMandate.inputs.earlyExitMinDuration.suffix', form.fees.earlyExitMinDuration ?? 0)"
              density="compact"
              hide-details="auto"
              type="number"
            />
          </v-col>
        </v-row>
      </v-stepper-window-item>
    </v-stepper-window>
  </v-stepper>
</template>
